/**
 * React context for MobX stores.
 *
 * Provides the root store to all components via context.
 */

import { createContext, useContext, type ReactNode } from 'react';

import { RootStore, rootStore } from './RootStore';

/**
 * React context for the MobX root store.
 */
const StoreContext = createContext<RootStore | null>(null);

/**
 * Props for the StoreProvider component.
 */
interface StoreProviderProps {
  children: ReactNode;
  store?: RootStore;
}

/**
 * Provider component that makes the MobX stores available to all children.
 *
 * @example
 * ```tsx
 * <StoreProvider>
 *   <App />
 * </StoreProvider>
 * ```
 */
export function StoreProvider({ children, store = rootStore }: StoreProviderProps): ReactNode {
  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
}

/**
 * Hook to access the root store from any component.
 *
 * @returns The root store instance
 * @throws Error if used outside of StoreProvider
 *
 * @example
 * ```tsx
 * const Component = observer(function Component() {
 *   const { appStore } = useStores();
 *   return <div>{appStore.isConnected ? 'Connected' : 'Disconnected'}</div>;
 * });
 * ```
 */
export function useStores(): RootStore {
  const store = useContext(StoreContext);

  if (!store) {
    throw new Error('useStores must be used within a StoreProvider');
  }

  return store;
}

/**
 * @deprecated Use useStores() instead
 */
export const useStore = useStores;

/**
 * Hook to access just the app store.
 *
 * @returns The app store instance
 */
export function useAppStore(): RootStore['appStore'] {
  const { appStore } = useStores();
  return appStore;
}

/**
 * Hook to access just the auth store.
 *
 * @returns The auth store instance
 */
export function useAuthStore(): RootStore['authStore'] {
  const { authStore } = useStores();
  return authStore;
}

/**
 * Hook to access just the preferences store.
 *
 * @returns The preferences store instance
 */
export function usePreferencesStore(): RootStore['preferencesStore'] {
  const { preferencesStore } = useStores();
  return preferencesStore;
}

/**
 * Hook to access just the entitlements store.
 *
 * @returns The entitlements store instance
 */
export function useEntitlementsStore(): RootStore['entitlementsStore'] {
  const { entitlementsStore } = useStores();
  return entitlementsStore;
}

/**
 * Hook to access just the character store.
 *
 * @returns The character store instance
 */
export function useCharacterStore(): RootStore['characterStore'] {
  const { characterStore } = useStores();
  return characterStore;
}

/**
 * Hook to access just the game store.
 *
 * @returns The game store instance
 */
export function useGameStore(): RootStore['gameStore'] {
  const { gameStore } = useStores();
  return gameStore;
}

/**
 * Hook to access just the UI store.
 *
 * @returns The UI store instance
 */
export function useUIStore(): RootStore['uiStore'] {
  const { uiStore } = useStores();
  return uiStore;
}

/**
 * Hook to access just the notification store.
 *
 * @returns The notification store instance
 */
export function useNotificationStore(): RootStore['notificationStore'] {
  const { notificationStore } = useStores();
  return notificationStore;
}

/**
 * Hook to access all stores (alias for useStores).
 *
 * @returns The root store instance
 */
export function useRootStore(): RootStore {
  return useStores();
}
